import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GeometryType } from "@evergis/api";
import { FeatureLayer, SrId, useMapContext } from "@evergis/react";
import { Point } from "@evergis/sgis";
import { booleanPointInPolygon, point, polygon } from "@turf/turf";
import { BackButton, Card, Flex, FlexSpan, H3, Link, Loader, OutlineButton } from "../../../ui";
import { CardHeader, EmptyPlaceholder, MapContainer } from "./styled";
import { ClusterDistrictControl } from "./ClusterDistrictControl";
import { SimpleLegendControl } from "../../../components/SimpleLegendControl";
import { Layout } from "../../../components/Layout";
import { Map } from "../../../components/Map";
import { BusinessRetailList } from "./BusinessRetailList";
import { SimpleFeaturesLayer } from "../../../components/SimpleFeaturesLayer";
import { DIALOGS } from "../../../constants/dialogs";
import { RetailBusinessUrl } from "../../../constants/routes";
import { MAP_CONTROL_LEFT_DESKTOP } from "../../../constants/layout";
import { SELECTED_POLYGON_SYMBOL } from "../../../constants/map";
import { useCurrentCity, useCurrentFeature, useDialog, useIsDesktop, useMeasurerActive, useResetOverlayFeatureOnLoad, useUser, } from "../../../hooks";
import { getPolygonFeature } from "../../../utils";
import { getPolygonHalo } from "../../../utils/map";
import { api } from "../../../api";
import { RetailTab } from "./ClusterDistrictControl/types";
import { LegendSymbolType } from "../../../components/SimpleLegendControl/types";
import { AppSection } from "../../../slices/app/types";
import position from "../../../assets/images/position.svg";
// eslint-disable-next-line max-lines-per-function
export const RetailIntegralLocationPage = () => {
    const isDesktop = useIsDesktop();
    const [user] = useUser();
    const { map } = useMapContext();
    const [currentCity] = useCurrentCity();
    const navigate = useNavigate();
    const [tab, setTab] = useState(RetailTab.Grid);
    const [hexList, setHexList] = useState([]);
    const [districtList, setDistrictList] = useState([]);
    const [styles, setStyles] = useState();
    const [currentFeature, setCurrentFeature] = useCurrentFeature();
    const [loading, setLoading] = useState(false);
    const [clickPoint, setClickPoint] = useState();
    const openDialog = useDialog();
    const [isMeasurerActive] = useMeasurerActive();
    const onMapClick = useCallback(e => {
        if (isMeasurerActive) {
            return;
        }
        setClickPoint(e.point.position);
    }, [isMeasurerActive]);
    const getLayerStyle = useCallback((styleId) => {
        if (!styles)
            return null;
        return styles.find(style => style.styleId === styleId);
    }, [styles]);
    const onClickBack = useCallback(() => {
        setCurrentFeature(undefined);
    }, [setCurrentFeature]);
    const hexFeatures = useMemo(() => hexList
        .filter(item => item.id !== currentFeature?.id)
        .map(item => getPolygonFeature(item.geometry.coordinates, {
        ...getLayerStyle(item.properties.styleId),
    })), [currentFeature?.id, getLayerStyle, hexList]);
    const districtFeatures = useMemo(() => districtList
        .filter(item => item.id !== currentFeature?.id)
        .map(item => getPolygonFeature(item.geometry.coordinates[0], {
        ...getLayerStyle(item.properties.styleId),
    })), [currentFeature?.id, districtList, getLayerStyle]);
    const renderCurrentFeature = useMemo(() => {
        if (!currentFeature) {
            return null;
        }
        const coordinates = tab === RetailTab.Grid
            ? currentFeature.geometry.coordinates
            : currentFeature.geometry.coordinates[0];
        return (_jsx(FeatureLayer, { features: [
                getPolygonHalo(coordinates),
                getPolygonFeature(coordinates, {
                    ...getLayerStyle(currentFeature.properties.styleId),
                    ...SELECTED_POLYGON_SYMBOL,
                }),
            ], zIndex: 100 }));
    }, [currentFeature, getLayerStyle, tab]);
    useResetOverlayFeatureOnLoad();
    useEffect(() => {
        map.on("click", onMapClick);
        return () => {
            map.off("click", onMapClick);
        };
    }, [isMeasurerActive]); // eslint-disable-line
    useEffect(() => {
        if (!currentCity?.id) {
            return;
        }
        setLoading(true);
        setCurrentFeature(undefined);
        setStyles(undefined);
        setHexList([]);
        setDistrictList([]);
        switch (tab) {
            case RetailTab.Grid:
                api.service
                    .getRetailHexService(currentCity.id, user.guid)
                    .then(response => {
                    setHexList(response.features.features);
                    setStyles(response.styles);
                })
                    .finally(() => {
                    setLoading(false);
                });
                break;
            case RetailTab.District:
                api.service
                    .getRetailDistrictService(currentCity.id, user.guid)
                    .then(response => {
                    setDistrictList(response.features.features);
                    setStyles(response.styles);
                })
                    .finally(() => {
                    setLoading(false);
                });
                break;
            default:
        }
    }, [currentCity?.id, tab]); // eslint-disable-line
    useEffect(() => {
        if (!clickPoint || (!hexList.length && !districtList.length)) {
            return;
        }
        const turfPoint = point(clickPoint);
        let intersectObject = null;
        if (tab === RetailTab.Grid) {
            hexList.some(item => {
                const turfCoordinates = item.geometry.coordinates[0].map(coordinate => new Point([...coordinate].reverse()).projectTo(map.crs).position);
                const turfPolygon = polygon([turfCoordinates]);
                if (booleanPointInPolygon(turfPoint, turfPolygon)) {
                    intersectObject = item;
                    return true;
                }
            });
        }
        else if (tab === RetailTab.District) {
            districtList.some(item => {
                const turfCoordinates = item.geometry.coordinates[0][0].map(coordinate => new Point([...coordinate].reverse()).projectTo(map.crs).position);
                const turfPolygon = polygon([turfCoordinates]);
                if (booleanPointInPolygon(turfPoint, turfPolygon)) {
                    intersectObject = item;
                    return true;
                }
            });
        }
        if (intersectObject) {
            setCurrentFeature(intersectObject);
        }
    }, [clickPoint]); // eslint-disable-line
    return (_jsx(Layout, { section: AppSection.RetailIntegral, noPadding: !isDesktop, leftPanel: _jsx(Flex, { column: true, height: "100%", children: _jsx(FlexSpan, { height: "100%", children: _jsx(Card, { height: "100%", children: currentFeature ? (_jsxs(_Fragment, { children: [_jsx(CardHeader, { children: _jsx(BackButton, { label: "\u041D\u0430\u0437\u0430\u0434", onClick: onClickBack }) }), _jsx(H3, { children: "\u041D\u0430\u0438\u0431\u043E\u043B\u0435\u0435 \u0432\u043E\u0441\u0442\u0440\u0435\u0431\u043E\u0432\u0430\u043D\u043D\u044B\u0435 \u0432\u0438\u0434\u044B \u0431\u0438\u0437\u043D\u0435\u0441\u0430 \u043D\u0430 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0439 \u0442\u0435\u0440\u0440\u0438\u0442\u043E\u0440\u0438\u0438" }), _jsx(BusinessRetailList, { currentFeature: currentFeature })] })) : (_jsxs(_Fragment, { children: [_jsx(OutlineButton, { buttonSize: "s", width: "100%", onClick: () => navigate(RetailBusinessUrl.path), children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043A \u0441\u043F\u0438\u0441\u043A\u0443 \u0431\u0438\u0437\u043D\u0435\u0441\u043E\u0432" }), _jsx(Flex, { column: true, flex: "1", alignItems: "center", justifyContent: "center", mt: "2rem", mb: "2rem", children: loading ? (_jsx(Loader, {})) : (_jsxs(_Fragment, { children: [_jsx("img", { src: position, width: "50px", height: "50px", alt: "" }), _jsx(EmptyPlaceholder, { children: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0438\u043D\u0442\u0435\u0440\u0435\u0441\u0443\u044E\u0449\u0443\u044E \u0442\u0435\u0440\u0440\u0438\u0442\u043E\u0440\u0438\u044E \u0434\u043B\u044F \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u044F \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u0438 \u043E \u043D\u0430\u0438\u0431\u043E\u043B\u0435\u0435 \u0432\u043E\u0441\u0442\u0440\u0435\u0431\u043E\u0432\u0430\u043D\u043D\u044B\u0445 \u0432\u0438\u0434\u0430\u0445 \u0431\u0438\u0437\u043D\u0435\u0441\u0430 \u043F\u043E\u00A0\u0441\u043E\u043E\u0442\u043D\u043E\u0448\u0435\u043D\u0438\u044E \u0441\u043F\u0440\u043E\u0441\u0430 \u0438\u00A0\u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u044F" }), tab === RetailTab.Grid && (_jsx(FlexSpan, { ml: "auto", mr: "auto", alignSelf: "baseline", mt: "1rem", children: _jsx(Link, { icon: "info_modal2", iconPlacement: "right", onClick: e => {
                                                    e.stopPropagation();
                                                    openDialog(DIALOGS.RETAIL_INTEGRAL_INFO_DIALOG);
                                                }, children: "\u0418\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F \u043E \u043C\u0435\u0442\u043E\u0434\u0438\u043A\u0435" }) }))] })) })] })) }) }) }), children: _jsx(MapContainer, { children: _jsxs(Map, { width: "100%", height: "100%", extent: {
                    ...currentCity?.geometry,
                    coordinates: [...(currentCity?.geometry.coordinates ?? [])],
                    type: GeometryType.Polygon,
                    sr: SrId.Wgs84,
                }, children: [_jsx(ClusterDistrictControl, { tab: tab, setTab: setTab }), isDesktop && (_jsx(SimpleLegendControl, { bottom: "1.5rem", left: MAP_CONTROL_LEFT_DESKTOP, labelFrom: (_jsxs(FlexSpan, { children: ["\u041D\u0430\u0438\u043C\u0435\u043D\u0435\u0435", _jsx("br", {}), "\u043F\u0440\u0438\u0432\u043B\u0435\u043A\u0430\u0442\u0435\u043B\u044C\u043D\u044B\u0439"] })), labelTo: (_jsxs(FlexSpan, { children: ["\u041D\u0430\u0438\u0431\u043E\u043B\u0435\u0435", _jsx("br", {}), "\u043F\u0440\u0438\u0432\u043B\u0435\u043A\u0430\u0442\u0435\u043B\u044C\u043D\u044B\u0439"] })), symbolType: LegendSymbolType.Hexagon, symbols: [
                            { color: "#60519ccc", opacity: 1 / 5 },
                            { color: "#60519ccc", opacity: 2 / 5 },
                            { color: "#60519ccc", opacity: 3 / 5 },
                            { color: "#60519ccc", opacity: 4 / 5 },
                            { color: "#60519ccc", opacity: 5 / 5 },
                        ] })), renderCurrentFeature, tab === RetailTab.Grid && _jsx(SimpleFeaturesLayer, { features: hexFeatures }), tab === RetailTab.District && _jsx(SimpleFeaturesLayer, { features: districtFeatures })] }) }) }));
};
